import React, { useState } from "react";
import { getLeagueMedia } from "../landing/leagueImage";
import { stripHtml } from "../landing/leagueStatus";
import {
  assetLabel,
  assetTypeLabel,
  leagueTypeLabel,
  memberCountLabel,
  metaLine,
  organizerHandle,
} from "../landing/leagueFormat";
import { memberCount } from "../landing/inviteCode";
import CreatedByBlock from "../landing/CreatedByBlock";
import { leagueDashboardPath } from "../../../utils/leaguePath";
import LeagueStatusLine from "../landing/LeagueStatusLine";
import { CashPrizeMetaLine } from "../landing/CashPrizeCard";

// The banner across the top of a league on desktop — when there is one.
//
// Not every league has artwork. A public league never uploads any: the create
// flow rasterises its chosen icon to a 256px square and sends that as the league
// image. Stretching that across 1200px looked like a rendering fault, and boxing
// it on a teal slab just moved the problem — a 176px band carrying one small
// glyph is a lot of furniture for no information.
//
// So the banner asks the file what it is. Wider than it is tall means real
// artwork, and it fills the band. A square means an icon, and there is no band
// at all: the league opens on its name, which is what the design shows.
//
// The image is loaded either way (display:none does not stop a fetch), so the
// answer arrives without a second request and without a slab flashing first.
const LeagueHero = ({ media, showOnMobile = false }) => {
  const [isBanner, setIsBanner] = useState(false);

  // Client Rev 2, desktop Private League Home 4: "If no image was uploaded,
  // collapse the image area completely. Do not show a placeholder."
  // `hasUploadedMedia` is false when getLeagueMedia fell back to its bundled
  // default, so this returns nothing at all rather than a band of default art.
  if (!media?.hasUploadedMedia) return null;

  return (
    <div
      className={
        isBanner
          ? // 4: "reduce the desktop cover height to roughly 160 to 180px" —
            // md:h-44 is 176px. Mobile keeps the shorter 144px band.
            `${showOnMobile ? "block" : "hidden md:block"} h-36 md:h-44 overflow-hidden bg-[#0F4146]`
          : "hidden"
      }
    >
      <img
        src={media.url}
        alt=""
        className="h-full w-full object-cover"
        onLoad={(e) => {
          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
          setIsBanner(!!w && !!h && w / h > 1.2);
        }}
        onError={() => setIsBanner(false)}
      />
    </div>
  );
};

// The organiser block moved to landing/CreatedByBlock — client Rev: "CREATED BY is
// a single League Play component; make sure this block matches on all pages where
// it exists." What was here WAS the approved treatment, so the shared component is
// this markup lifted verbatim; this screen renders identically to before.

// Inside a league. The identity header is the same on every league screen, so it
// lives here and each screen supplies its own body.
//
// A league is a child of the app: everything you can do outside, you can do in
// here — but scoped to THIS league. Navigating between those things is the LEFT
// RAIL's job now (see LeagueMenu): while you're a member, that column stops being
// the app's nav and becomes the league's, which is why this header carries no
// tabs of its own.
//
// Trade is /:slug/trade: the app's real Trade screen (chart + order ticket) run
// inside LeagueTradeContext, so its right-hand list holds the league's assets and
// its orders carry the league's game_id. Deliberately NOT /:slug/charts, which
// loads the main account's watchlists and reads the slug as a symbol.
//
// `active` is still accepted so screens can keep declaring which section they
// are; the rail derives its own highlight from the URL.
// `topOffset` clears whatever fixed header is above the shell. The default
// clears the app's DashboardHeader; the focused League Details screen replaces
// that with its own 64px back-arrow header and passes a matching offset.
// `compact` reduces the header to the eyebrow and the league name.
//
// Client Rev 2 says the same thing about three screens — Portfolio (2 & 3),
// Watchlist (2 & 3) and Leaderboard (2): "Below the QUSHi header show only:
// PRIVATE LEAGUE / League title", and "Do not repeat tagline, Created by,
// creator avatar, Starts in / Ends in, or the league metadata line. Those belong
// on League Home only."
//
// One flag rather than a second header component, so the eyebrow and the title
// can never drift between Home and the screens beside it.
// `standalone` is for the league screens that are NOT routed under /contest —
// /:slug/watchlist and /:slug/leaderboard. Every /contest/:slug/* screen renders
// inside PageContainer, which is what clears the fixed 256px desktop sidebar
// (md:ml-64) and adds the px-2 gutter. The two routes above are mounted at the
// app root with no PageContainer, so this shell centred itself in the FULL
// viewport and slid straight under the sidebar.
//
// Measured at 1440px BEFORE: the identity block's left edge was 104px on
// /:slug/leaderboard and /:slug/watchlist — inside a 256px sidebar — against 288px
// on /contest/:slug/portfolio. Client Rev 3, desktop private league: "Leaderboard
// screen is all jacked up… add an identity block, left-aligned above the
// Leaderboard screen title. The identity block will sit at the exact distance we
// use for all the other screens - portfolio and watchlist." 288px IS that
// distance, so this reproduces PageContainer's offset rather than inventing one.
// Desktop only — mobile has no sidebar and is left exactly as it was.
/**
 * The league identity block: competition eyebrow, dynamic league title under it.
 *
 * Exported because it is now used in two places and the client asked for them to
 * be identical: the shell's own compact screens (Portfolio, Watchlist,
 * Leaderboard, Profile) and the organiser's League settings screen.
 *
 * Client Rev 5: "The Organizer's League settings link. I like the dynamic league
 * title left-aligned as you have it, but let's match the exact identity block
 * that we use in the portfolio and watchlist screens: same size, color, weight,
 * etc. PRIVATE LEAGUE eyebrow with the dynamic league title under it — same
 * identity block."
 *
 * "Exact" is why this is one component rather than a second copy of the class
 * strings: a copy is the thing that drifts. Both callers render THIS.
 */
export const LeagueIdentityBlock = ({ game, className = "" }) => {
  const typeLabel = (leagueTypeLabel(game) || "").toUpperCase();
  return (
    <div className={className}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
        {typeLabel}
      </p>
      <h1 className="mt-1 min-w-0 [overflow-wrap:anywhere] font-montserrat font-bold text-pageTitle leading-tight text-[22px]">
        {game?.title}
      </h1>
    </div>
  );
};

const LeagueShell = ({
  game,
  active = "home",
  children,
  compact = false,
  standalone = false,
  topOffset = "mt-16 md:mt-24",
  // Rendered ABOVE the identity block rather than below it. Exists for one
  // caller: the standalone league profile's back arrow, which has to sit above
  // the eyebrow and the league name the way a back arrow does everywhere else in
  // the app. Passing it as a child put it under the title, because children are
  // the screen's BODY. Nothing else supplies it, so every other league screen
  // renders exactly as before.
  leading = null,
  // The desktop right rail, when the screen wants it to start level with the
  // IDENTITY HEADER rather than below it.
  //
  // Client, Cash Game competition home: "Prize card becomes the first card in
  // the right column. Its top edge aligns with the header title row (the CASH
  // GAME eyebrow line), so the two columns start on the same top edge… The
  // Leaderboard card sits on the same top edge as the Game feed header on the
  // left."
  //
  // LeagueColumns puts its rail beside the screen's BODY, which begins under the
  // identity header and its divider — so the rail could never reach the eyebrow
  // line from there, and the space beside the header stayed empty (the client
  // circled it). Passing the rail here instead moves the grid up to enclose the
  // header, which is the only way the two columns can share a top edge.
  //
  // Only League Home for a cash game supplies it. Every other screen passes
  // nothing and renders exactly as before.
  rail = null,
}) => {
  // Client: "desktop version has the old grey line at the top; please remove the
  // grey line" — on the league profile, and on every standalone league screen.
  //
  // Measured on /<slug>/profile/<id> at 1920x993 BEFORE: the fixed app header
  // ends at y=50, this container starts at y=96, and the 46px between them was
  // .adminLayout's own #f1f5f9 showing through. That band IS the grey line.
  //
  // The cause is that the offset is a MARGIN on the very element that paints the
  // white surface. Margin is outside the painted box, so the space it reserves to
  // clear the header is transparent by definition — the shell can paint itself
  // white all it likes and the gap above it stays grey. It only looks fine when
  // the header happens to be exactly as tall as the offset, which it is not: the
  // header is a py-3 flex row whose height is whatever its contents come to.
  //
  // So for `standalone` — the screens that carry their own surface (Leaderboard,
  // Watchlist, league Portfolio, league Profile) — the same offset is applied as
  // PADDING. Identical spacing, but now inside the box, so the cleared band is
  // painted with the rest of the screen and cannot show the shell through it at
  // any header height. Screens that are NOT standalone sit inside PageContainer,
  // which already paints behind them, and keep the margin exactly as it was.
  const offsetClasses = standalone ? topOffset.replace(/\bmt-/g, "pt-") : topOffset;

  const media = getLeagueMedia(game);

  const tagline = stripHtml(game?.tagline || "");
  // The redesigned header is the PUBLIC league's. Private keeps the header it
  // already had — see the branch below.
  const isPublic = game?.game_type !== "private";
  // Which kind of competition this is, as the eyebrow above the name.
  const typeLabel = (leagueTypeLabel(game) || "").toUpperCase();
  // Private's subtitle falls back to organiser + assets when it has no tagline.
  // Handle only — QUSHi never displays a user's real name.
  const organiser = game?.user?.user_name || "";
  const subtitle = [organiser, assetTypeLabel(game)].filter(Boolean).join(" • ");

  // Client Rev 1: "View League: opens the newly submitted league landing page in
  // its Pending approval state. […] We just need to add pending while pending."
  // The organiser is a member of their own league from the moment it exists
  // (GameController::enrolOrganiser), so this header — not the pre-join card — is
  // what they actually land on after submitting, and it is where the state has to
  // be said out loud. It replaces the clock rather than joining it: a pending
  // league's dates are provisional until an admin approves it, so getLeagueStatus
  // deliberately gives it no countdown to show — and LeagueStatusLine renders
  // whichever of the two applies.
  const members = memberCount(game);
  const isCash = !!game?.is_cash_prize_league;
  // What's tradable and how many are in — one quiet line under the organiser.
  // assetLabel is empty until asset_category exists on the backend, so it falls
  // back to what the league actually allows.
  // Client 0014889: "3 members -> 3 entrants" on a Cash Game. memberCountLabel
  // carries the noun and its plural together so the count and the word can
  // never be pluralised against different vocabularies.
  const metaText = metaLine(
    assetLabel(game) || assetTypeLabel(game),
    memberCountLabel(game, members)
  );

  return (
    // The Contest wrapper renders no heading band for a league's own screens, so
    // the shell clears the fixed header itself (see `topOffset`).
    // Client Rev 4: "Why do the leaderboard and watchlist have a grey
    // background?" Because `.adminLayout` paints the app shell #f1f5f9 and
    // nothing above those two screens painted over it. Every other league screen
    // sits under /contest/*, where PageContainer supplies `shadow-md bg-white`;
    // Leaderboard and Watchlist are mounted at /:slug/... instead, outside that
    // tree — the same root cause as the sidebar overlap `standalone` was added
    // for. So `standalone` now carries the surface as well as the offset: the
    // same bg-white and shadow-md PageContainer gives the others.
    <div
      className={`${offsetClasses} pb-12 ${
        standalone ? "md:ml-64 md:px-2 bg-white shadow-md" : ""
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        {leading}
        {/* ONE identity header for both league types.
            Client Rev 2: "Follow all the same rules you just completed in task
            (7)... All the same rules apply here, with swapping public for
            private." The public league home had already been redesigned; private
            was still on the header it had before that, which is why it had no
            PRIVATE LEAGUE eyebrow (desktop item 1), kept its organiser in the
            right rail instead of the header (item 2), and framed the cover and
            the identity as one card (item 5).

            Item 5: "I would also separate the image from the identity treatment.
            Cover image first, then an open white identity header underneath
            rather than making image + header feel like one giant card." So the
            card wrapper that used to enclose both is gone — the cover stands on
            its own and the identity below it is open, closed by a thin full-width
            divider.

            The cover only ever renders for a league that uploaded one, which in
            practice is private only: the create flow rasterises a public league's
            chosen icon to a square, and LeagueHero shows nothing for a square. */}
        {!compact && <LeagueHero media={media} showOnMobile={!isPublic} />}

        {/* The grid only exists when a screen supplies a `rail`; otherwise this
            renders the same single column it always did. Identical breakpoint
            and track sizes to LeagueColumns, so a cash game's rail is the same
            320px beside the same content column as every other league screen's.
            Below lg it collapses and the rail drops out entirely — the phone has
            no room for it, and the client's note is a desktop note. */}
        <Body rail={rail}>

        <div
          className={`${
            !compact && media.hasUploadedMedia ? "pt-4 md:pt-5" : ""
          } pb-4 md:pb-5`}
        >
          {/* Which kind of competition this is. Item 1: "Add the PRIVATE LEAGUE
              eyebrow above the league title, using the same muted all caps
              treatment as PUBLIC LEAGUE." Same node, same classes, both types.

              On the compact screens this pair IS LeagueIdentityBlock — the very
              component League settings renders — so "the exact identity block"
              the client asked for is a shared definition, not a lookalike. Home
              and League details keep their own larger title and their tagline /
              organiser / metadata lines below it. */}
          {compact && <LeagueIdentityBlock game={game} />}
          {!compact && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
            {typeLabel}
          </p>
          )}

          {/* On the compact screens the title stands alone — no countdown either,
              since "Starts in / Ends in" is on the client's do-not-repeat list.
              Item 3: "Keep Ends in 1d on the right side of the header, aligned to
              the outer right edge of the full content grid, same as Public League
              Home." With the enclosing card removed, this row's right edge IS the
              content column's right edge. */}
          {!compact && (
          <div className="mt-1 flex items-start justify-between gap-4">
            {/* `min-w-0` + overflow-wrap:anywhere is what keeps the countdown on
                screen when the title is one long unbroken word.
                A flex item defaults to `min-width: auto`, i.e. its MIN-CONTENT
                width, and `break-words` (overflow-wrap: break-word) does not
                reduce min-content — it only breaks a word that has already
                overflowed. So a title like "Testing privateeeeeeeeeeeeee" set a
                240px floor on this column, and since the countdown is shrink-0 the
                row simply grew past the container and pushed "Ends in 6d" off the
                right edge. Measured in Chrome with this exact title:

                  viewport 320px  →  countdown cut by 51px
                  viewport 360px  →  countdown cut by 11px
                  viewport 390px  →  fits

                `overflow-wrap: anywhere` DOES lower min-content (break-word does
                not — that is the whole difference between the two values), so with
                `min-w-0` the title column can shrink and the countdown always
                lands inside. 0px cut at all three widths after the change. */}
            {/* Client Rev 4: "Home: keep the current league title size — no
                change; current size looks like 24px. Portfolio, Watchlist,
                Leaderboard: reduce the dynamic league title by about 15% from
                the current Home size. Do 22px for these three screens."
                `compact` is exactly those three tabs, so the size follows the
                same flag that already strips the tagline, the organiser and the
                metadata from them — one switch, not a per-screen prop. */}
            <h1 className="min-w-0 [overflow-wrap:anywhere] font-montserrat font-bold text-pageTitle leading-tight text-2xl">
              {game?.title}
            </h1>
          </div>
          )}

          {/* Private falls back to organiser + assets when it has no tagline;
              public has no fallback because its create flow requires one. */}
          {!compact && (tagline || (!isPublic && subtitle)) && (
            <p className="mt-1 text-sm text-secondary [overflow-wrap:anywhere]">
              {tagline || subtitle}
            </p>
          )}

          {/* Item 2: "Move organizer avatar + Created by into the main league
              header, directly under the tagline." */}
          {/* Client Rev 4: "Status line — 'Ends in' and 'Starts in' need to be
              moved inline with the username in the identity block. This can not
              be in the league title line."
              So the countdown (and the Pending approval pill, which occupies the
              same slot) moved off the <h1> row and onto this one, right-aligned
              against the same content edge it had before.
              Alignment, and why it is `items-end` rather than `items-center`:
              CreatedByBlock is a TWO-line block — the CREATED BY eyebrow above the
              handle — so centring the countdown against the whole block lands it in
              the gap BETWEEN those lines, ~7px above the handle. Measured that way:
              handle centre y=228, countdown centre y=221. Off the title line, yes,
              but not "inline with the username", and the client spotted it.
              `items-end` pins the countdown's bottom to the block's bottom, and the
              block's last line IS the handle. Both are text-sm, so their line boxes
              are the same height and the two centres coincide exactly. */}
          {!compact && (
            <div className="mt-3 flex items-end justify-between gap-4">
              {/* Client: "Make the profile avatar on the public and private
                  league home next to CREATED BY go to the organizers' league
                  dashboard page so members can DM them easily."
                  leagueDashboardPath is the same helper the mobile header's
                  avatar already uses for "inside league play whenever click on
                  avatar it will move to league dashboard", so the organiser's
                  avatar lands on the same kind of screen a member's does. */}
              <CreatedByBlock
                game={game}
                avatarHref={
                  game?.vanity_url && game?.user?.id
                    ? leagueDashboardPath(game.vanity_url, game.user.id)
                    : null
                }
              />
              {/* Client Rev 4, point 1: "The status line on the league home page
                  is in line with the organizer's identity block's username, but
                  check the color against our league play status line token rule."
                  The alignment above is right and untouched; the colour was not —
                  this row picked #22A36B / #0F4146, neither of which is in the
                  locked set. LeagueStatusLine reads previewStatus instead, so this
                  screen no longer holds an opinion about status colours (and the
                  Pending approval pill comes from the same place). */}
              {/* Client, Cash Game home: "Move the `Starts in Xh` status out
                  of the header. It floats alone in dead space right now
                  (circled). It goes into the prize card, top row."

                  Scoped to the screens that HAVE a prize card to move it into —
                  `rail` is non-null only on a cash game's Home — and only at the
                  width where that card exists. Below lg there is no rail, so the
                  countdown stays here, which is the only place a phone shows it. */}
              <LeagueStatusLine game={game} className={rail ? "lg:hidden" : ""} />
            </div>
          )}

          {/* Client Rev 2, League Home/Feed 1: "Metadata - metadata needs to have
              the same starting margin as the username and organizer handle. Right
              now, it shares the profile pic's margin."
              The handle starts past OrganizerBlock's 36px avatar plus its 10px
              gap, so 46px is that edge. Written as one padding value rather than
              nesting the line inside the block, because the metadata is a fact
              about the LEAGUE, not about the organiser. */}
          {!compact && metaText && (
            <p className="mt-2 pl-[2.875rem] text-sm text-secondary">{metaText}</p>
          )}

          {/* The prize, as a line rather than a card — the phone's answer to the
              rail's prize card. Client: "for the mobile version of a cash game
              home competition page we will put the prize info in a meta line
              under the `Mixed · 2 entrants`".

              `lg:hidden` because above that width the prize card in the rail is
              the same fact, and the client put it in exactly one place on each
              breakpoint. Same left edge as the metadata line above it. */}
          {!compact && isCash && (
            <CashPrizeMetaLine
              game={game}
              className="mt-1 pl-[2.875rem] lg:hidden"
            />
          )}
        </div>

        {/* Item 5's closing move: "thin full width divider" between the identity
            header and whatever the screen puts below it.

            Client Rev 5: "DESKTOP version: the leaderboard, watchlist, and
            profile screens have a grey header line. please remove."
            Those three are exactly the `compact` screens — the eyebrow-plus-name
            mode — so the rule follows the same flag that already strips the
            tagline, the organiser and the metadata from them, rather than three
            per-screen exceptions. That also covers Portfolio, the fourth compact
            screen, which the client has always grouped with the other three
            ("Portfolio, Watchlist, Leaderboard" — Rev 4) and which would
            otherwise be the only one still wearing the line.

            League Home and League details keep it: the client asked for the
            divider THERE in Rev 2, to separate the cover from the identity. */}
        {!compact && <div className="border-t border-divider" />}

        {children}
        </Body>
      </div>
    </div>
  );
};

// One column, or two with a rail. Written as a component rather than a ternary
// around the shell's whole body so the markup between them cannot drift.
const Body = ({ rail, children }) => {
  if (!rail) return <>{children}</>;
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6 lg:items-start">
      <div className="min-w-0">{children}</div>
      <aside className="hidden lg:block">{rail}</aside>
    </div>
  );
};

export default LeagueShell;
